import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { InvestingExperienceForm } from 'common/forms';

import { Typography } from 'common/components';
import { QuestionTitle, PageControls } from '../../../../components';
import { InvestingExperienceFormValues } from '../../../../../../common/models/Forms';
import { useDispatch, useSelector } from 'react-redux';
import { FormikProps } from 'formik';
import { useRef, useState } from 'react';
import { setInvestingExperienceValuesAction, selectInvestingExperience } from '../../../../ducks';
import { TableGroupResult } from 'common/components/Form/Radio';
import { useApplyFinalValidationOnBind } from '../../../../hooks/useApplyFinalValidationOnBind';
import { createSoftContinueSession, runSoftFormStepContinue } from '../../../../validation/softContinue';
import { FeatureProps } from '../../../../index';

export default function InvestingExperienceFeature({ isPrivileged }: FeatureProps) {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const initialValues = useSelector(selectInvestingExperience);

  const formHelpersRef = useRef<{
    formik: FormikProps<InvestingExperienceFormValues>;
    groupResults: TableGroupResult[];
  } | null>(null);
  const softContinueSessionRef = useRef(createSoftContinueSession());
  const [showSoftContinueHint, setShowSoftContinueHint] = useState(false);
  const { onFormikBound } = useApplyFinalValidationOnBind();

  function handleBackClick() {
    navigate(-1);
  }

  async function handleContinueClick() {
    const helpers = formHelpersRef.current;
    if (!helpers) {
      return;
    }
    const session = softContinueSessionRef.current;
    const proceeded = await runSoftFormStepContinue(
      helpers.formik,
      (values) => {
        handleSubmit({ ...values, table: helpers.groupResults }, helpers.groupResults);
      },
      session,
    );
    setShowSoftContinueHint(!proceeded && session.proceedDespiteErrorsAcknowledged);
  }

  function bindSubmit(helpers: {
    formik: FormikProps<InvestingExperienceFormValues>;
    groupResults: TableGroupResult[];
  }) {
    formHelpersRef.current = helpers;
    onFormikBound(helpers.formik);
  }

  function handleSubmit(values: InvestingExperienceFormValues, table: TableGroupResult[]) {
    if (!isPrivileged) {
      dispatch(setInvestingExperienceValuesAction({ ...values, table }));
    }
    navigate(`/form/${id}/investing/hazard`);
  }
  return (
    <>
      <QuestionTitle prefix="I.">{t('investingFeature.experience.title')}</QuestionTitle>
      <Typography.Paragraph style={{ marginBottom: '50px' }}>
        {t('investingFeature.experience.content')}
      </Typography.Paragraph>
      <InvestingExperienceForm
        initialValues={initialValues || undefined}
        bindSubmit={bindSubmit}
        onSubmit={handleSubmit}
      />
      <PageControls softContinueHint={showSoftContinueHint} onBack={handleBackClick} onContinue={handleContinueClick} />
    </>
  );
}
